import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, DoorOpen, Layers, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { StatCard } from '@/components/shared/stat-card';
import { VenueSelect } from '@/components/shared/venue-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { displaySmartGroupName } from '@/lib/group-display';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { formatNumber } from '@/utils/format';

import {
  audiencePayload,
  emptyAudience,
  GroupAudienceFields,
  type GroupAudienceValue,
} from './group-audience-fields';
import { linkedAutoFillDefaultName } from './group-linked-notice';
import { GroupLargestCharts } from './group-largest-charts';
import { GroupsSubnav } from './groups-subnav';

interface GroupRow {
  id: string;
  name: string;
  type: 'manual' | 'template';
  isOpen: boolean;
  isActive: boolean;
  meetingVenueId: string | null;
  meetingVenueName: string | null;
  memberCount: number;
  pendingJoinRequestCount: number;
  linkedGroupId: string | null;
  linkedGroupName: string | null;
  linkedGroupType: 'manual' | 'template' | null;
}

interface ChurchGroupInsights {
  groupCount: number;
  membershipCount: number;
  openGroupCount: number;
  pendingJoinRequestCount: number;
  largest: Array<{ key: string; label: string; value: number }>;
  largestManual: Array<{ key: string; label: string; value: number }>;
  largestAutoFilled: Array<{ key: string; label: string; value: number }>;
}

export const ManualGroupsPage = () => {
  const { t } = useTranslation('groups');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const membership = useChurchStore((state) => state.membership);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [meetingVenueId, setMeetingVenueId] = useState<string | null>(null);
  const [audience, setAudience] = useState<GroupAudienceValue>(emptyAudience);
  const [createMatchingAutoFill, setCreateMatchingAutoFill] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const canCreateLinked = useMemo(
    () =>
      allowsFeature('smart_groups') &&
      hasPermission(membership, PERMISSIONS.GROUPS_MANAGE_TEMPLATES),
    [allowsFeature, membership],
  );

  const resetForm = () => {
    setName('');
    setIsOpen(false);
    setIsActive(true);
    setMeetingVenueId(null);
    setAudience(emptyAudience());
    setCreateMatchingAutoFill(false);
  };

  const list = useQuery({
    queryKey: ['groups', 'manual', 'group', page, pageSize],
    queryFn: () =>
      api.get<{
        data: GroupRow[];
        page: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/groups?type=manual&category=group&page=${page}&pageSize=${pageSize}`),
  });

  const insights = useQuery({
    queryKey: ['groups', 'insights', 'manual', 'group'],
    queryFn: () => api.get<ChurchGroupInsights>('/groups/insights?type=manual&category=group'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<GroupRow>('/groups', {
        name,
        type: 'manual',
        category: 'group',
        isOpen,
        isActive,
        meetingVenueId,
        ...audiencePayload(audience),
        ...(createMatchingAutoFill
          ? {
              createMatchingAutoFill: true,
              linkedAutoFillName: linkedAutoFillDefaultName(name, t),
            }
          : {}),
      }),
    onSuccess: () => {
      toast.success(t('manual.saved'));
      setOpen(false);
      resetForm();
      void queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<GroupRow>[] = [
    {
      id: 'name',
      header: t('columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'members',
      header: t('columns.members'),
      cell: (row) => t('members', { count: row.memberCount }),
    },
    {
      id: 'pendingJoinRequests',
      header: t('columns.pendingJoinRequests'),
      cell: (row) =>
        row.pendingJoinRequestCount > 0
          ? t('pendingJoinRequests', { count: row.pendingJoinRequestCount })
          : '—',
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'neutral'}>
          {row.isActive ? t('statusActive') : t('statusInactive')}
        </Badge>
      ),
    },
    {
      id: 'linked',
      header: t('linked.column'),
      cell: (row) =>
        row.linkedGroupName ? (
          <span className="text-body-sm text-fg-secondary">
            {t('linked.linkedAutoFill', { name: displaySmartGroupName(row.linkedGroupName) })}
          </span>
        ) : (
          '—'
        ),
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('manual.description')}
        tipId="groups.leadershipScope"
        headerActions={
          hasPermission(membership, PERMISSIONS.GROUPS_CREATE) ? (
            <Button onClick={() => setOpen(true)}>{t('manual.create')}</Button>
          ) : undefined
        }
      />

      <GroupsSubnav />

      <CollapsibleInsights
        summary={[
          {
            label: t('manual.insights.groups'),
            value: insights.data ? formatNumber(insights.data.groupCount) : '…',
          },
          {
            label: t('manual.insights.memberships'),
            value: insights.data ? formatNumber(insights.data.membershipCount) : '…',
          },
          {
            label: t('manual.insights.openGroups'),
            value: insights.data ? formatNumber(insights.data.openGroupCount) : '…',
          },
          {
            label: t('manual.insights.pendingJoinRequests'),
            value: insights.data ? formatNumber(insights.data.pendingJoinRequestCount) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={t('manual.insights.groups')}
            value={insights.data ? formatNumber(insights.data.groupCount) : undefined}
            icon={<Layers className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('manual.insights.memberships')}
            value={insights.data ? formatNumber(insights.data.membershipCount) : undefined}
            icon={<Users className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('manual.insights.openGroups')}
            value={insights.data ? formatNumber(insights.data.openGroupCount) : undefined}
            icon={<DoorOpen className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
          <StatCard
            label={t('manual.insights.pendingJoinRequests')}
            value={
              insights.data ? formatNumber(insights.data.pendingJoinRequestCount) : undefined
            }
            icon={<ClipboardList className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <GroupLargestCharts
          loading={insights.isLoading}
          largestManual={insights.data?.largestManual ?? []}
          largestAutoFilled={insights.data?.largestAutoFilled ?? []}
          labels={{
            autoFilledTitle: t('manual.insights.largestAutoFilled'),
            autoFilledEmpty: t('manual.insights.largestAutoFilledEmpty'),
            manualTitle: t('manual.insights.largestManual'),
            manualEmpty: t('manual.insights.largestManualEmpty'),
          }}
        />
      </CollapsibleInsights>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('manual.empty')}</p>}
        onRowClick={(row) => void navigate(`/groups/${row.id}`)}
      />

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          className="mt-4"
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) =>
              tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('manual.create')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t('manual.name')}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('meetingVenue')}</Label>
              <VenueSelect
                value={meetingVenueId}
                onChange={setMeetingVenueId}
                popoverPortal="dialog"
              />
            </div>
            <SwitchField
              id="manual-group-open"
              label={t('open')}
              checked={isOpen}
              onCheckedChange={setIsOpen}
            />
            <SwitchField
              id="manual-group-active"
              label={t('active')}
              checked={isActive}
              onCheckedChange={setIsActive}
            />
            {canCreateLinked ? (
              <>
                <SwitchField
                  id="manual-group-linked-auto-fill"
                  label={t('linked.createMatchingAutoFill')}
                  checked={createMatchingAutoFill}
                  onCheckedChange={setCreateMatchingAutoFill}
                />
                {createMatchingAutoFill ? (
                  <p className="text-body-sm text-fg-secondary">{t('linked.createMatchingAutoFillHint')}</p>
                ) : null}
              </>
            ) : null}
            <GroupAudienceFields value={audience} onChange={setAudience} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={create.isPending}>
                {t('manual.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
